import type { FinancialSnapshot, PeriodCarryover, TemporaryContribution, TemporaryContributionDestination, WeekBalance } from "./types";

const DAY_MILLISECONDS = 86_400_000;

function isoDayNumber(value: string | undefined): number | null {
  const match = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) return null;
  return Math.floor(timestamp / DAY_MILLISECONDS);
}

function isoDateFromDayNumber(dayNumber: number): string {
  return new Date(dayNumber * DAY_MILLISECONDS).toISOString().slice(0, 10);
}

export interface PeriodWeekRanges {
  week1: { start: string; end: string };
  week2: { start: string; end: string };
}

/** Equilibra los días: 14→7+7, 15→7+8 y 16→8+8. */
export function splitPeriodWeekRanges(periodStart: string | undefined, periodEnd: string | undefined): PeriodWeekRanges | null {
  const start = isoDayNumber(periodStart);
  const end = isoDayNumber(periodEnd);
  if (start == null || end == null || start >= end) return null;
  const totalDays = end - start + 1;
  const week1Days = Math.floor(totalDays / 2);
  if (week1Days < 1) return null;
  const week1End = start + week1Days - 1;
  const week2Start = week1End + 1;
  return {
    week1: { start: isoDateFromDayNumber(start), end: isoDateFromDayNumber(week1End) },
    week2: { start: isoDateFromDayNumber(week2Start), end: isoDateFromDayNumber(end) },
  };
}

export function localIsoDate(date = new Date()): string {
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");
}

/** La contabilidad usa siempre la fecha civil de CDMX, aunque el dispositivo esté de viaje. */
export function mexicoCityIsoDate(date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

/** Los recordatorios de rendimientos siguen la fecha civil del iPhone en Ciudad Juárez. */
export function juarezIsoDate(date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Ciudad_Juarez",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

/**
 * Conserva la cadencia histórica de Banculem: 1–14, 15–29 (o fin de febrero)
 * y 30–14. La excepción de febrero vuelve a iniciar el día 1 de marzo.
 */
export function periodEndForStart(start: string): string | null {
  const match = start.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) return null;

  let end: Date;
  if (day === 1) {
    end = new Date(Date.UTC(year, month - 1, 14));
  } else if (day === 15) {
    const monthEnd = new Date(Date.UTC(year, month, 0)).getUTCDate();
    end = new Date(Date.UTC(year, month - 1, Math.min(29, monthEnd)));
  } else if (day === 30) {
    end = new Date(Date.UTC(year, month, 14));
  } else {
    return null;
  }
  return [end.getUTCFullYear(), String(end.getUTCMonth() + 1).padStart(2, "0"), String(end.getUTCDate()).padStart(2, "0")].join("-");
}

/** Divide el presupuesto libre en dos mitades sin heredar ajustes anteriores. */
export function splitPeriodBudgetInHalves(totalCents: number): { week1Cents: number; week2Cents: number } {
  if (!Number.isSafeInteger(totalCents) || totalCents < 0) {
    throw new Error("El presupuesto de la quincena debe expresarse en centavos enteros no negativos.");
  }
  const week1Cents = Math.floor(totalCents / 2);
  return { week1Cents, week2Cents: totalCents - week1Cents };
}

/** El último día todavía pertenece a la quincena; el cierre inicia al siguiente. */
export function isPeriodReadyToClose(periodEnd: string | undefined, today = mexicoCityIsoDate()): boolean {
  const end = isoDayNumber(periodEnd);
  const current = isoDayNumber(today);
  return end != null && current != null && current > end;
}

/** Dinero de Nu que ya tiene un destino visible dentro de Banculem. */
export function visibleAllocationCents(snapshot: FinancialSnapshot): number {
  return snapshot.weeks.reduce((sum, week) => sum + Math.max(0, week.availableCents), 0)
    + Math.max(0, snapshot.savingsCents)
    + Math.max(0, snapshot.temporaryContributionCents)
    + snapshot.cardPayments.reduce((sum, card) => sum + Math.max(0, card.amountCents), 0)
    + Math.max(0, snapshot.previousPeriodCarryover?.availableCents ?? 0);
}

export function unclassifiedNuCents(snapshot: FinancialSnapshot): number {
  return Math.max(0, (snapshot.accounts[0]?.balanceCents ?? 0) - visibleAllocationCents(snapshot));
}

/** Último candado antes de guardar: una misma parte de Nu no puede clasificarse dos veces. */
export function assertAllocationsWithinNu(snapshot: FinancialSnapshot): FinancialSnapshot {
  const nuCents = snapshot.accounts[0]?.balanceCents ?? 0;
  const allocationCents = visibleAllocationCents(snapshot);
  if (allocationCents > nuCents) {
    const excess = (allocationCents - nuCents) / 100;
    throw new Error(`La operación clasificaría más dinero del que existe en Nu por ${excess.toLocaleString("es-MX", { style: "currency", currency: "MXN" })}. Revisa si el importe realmente ya estaba incluido.`);
  }
  return snapshot;
}

/** Deriva el calendario sin tocar saldos. Evita que una semana vencida siga recibiendo operaciones. */
export function snapshotForDate(
  snapshot: FinancialSnapshot,
  today = mexicoCityIsoDate(),
): FinancialSnapshot {
  const start = isoDayNumber(snapshot.periodStart);
  const end = isoDayNumber(snapshot.periodEnd);
  const current = isoDayNumber(today);
  const ranges = splitPeriodWeekRanges(snapshot.periodStart, snapshot.periodEnd);
  if (start == null || end == null || current == null || !ranges || snapshot.weeks.length < 2) return snapshot;

  const firstWeekEnd = isoDayNumber(ranges.week1.end)!;
  const secondWeekStart = isoDayNumber(ranges.week2.start)!;
  const statusFor = (weekId: WeekBalance["id"]): WeekBalance["status"] => {
    if (weekId === "week1") {
      if (current < start) return "upcoming";
      if (current <= firstWeekEnd) return "active";
      return "closed";
    }
    if (current < secondWeekStart) return "upcoming";
    if (current <= end) return "active";
    return "closed";
  };

  return {
    ...snapshot,
    periodDaysRemaining: Math.max(0, end - current + 1),
    weeks: snapshot.weeks.map((week) => {
      const status = statusFor(week.id);
      const weekStart = week.id === "week1" ? start : secondWeekStart;
      const weekEnd = week.id === "week1" ? firstWeekEnd : end;
      return {
        ...week,
        status,
        daysRemaining: status === "upcoming" ? Math.max(0, weekStart - current) : undefined,
        daysAfterToday: status === "active" ? Math.max(0, weekEnd - current) : undefined,
      };
    }),
  };
}

export function carryoverAtPeriodClose(
  snapshot: FinancialSnapshot,
  destinationPeriod: string,
  id: string,
  createdAt: string,
): PeriodCarryover | undefined {
  const previousAvailable = Math.max(0, snapshot.previousPeriodCarryover?.availableCents ?? 0);
  const closingWeek = snapshot.weeks.find((week) => week.id === "week2" && week.status !== "upcoming")
    ?? snapshot.weeks.find((week) => week.status === "active");
  const closingWeekCents = Math.max(0, closingWeek?.availableCents ?? 0);
  const total = previousAvailable + closingWeekCents;
  if (!total) return undefined;
  return {
    id,
    sourcePeriod: snapshot.periodLabel ?? "Quincena anterior",
    destinationPeriod,
    originalAmountCents: total,
    availableCents: total,
    transferredCents: 0,
    priorCarryoverCents: previousAvailable,
    closingWeekCents,
    createdAt,
  };
}

export function applyPreviousPeriodCarryover(
  snapshot: FinancialSnapshot,
  amountCents: number,
): FinancialSnapshot {
  const amount = Math.round(amountCents);
  const carryover = snapshot.previousPeriodCarryover;
  const available = Math.max(0, carryover?.availableCents ?? 0);
  if (!carryover || !available) throw new Error("La quincena anterior ya no tiene dinero disponible.");
  if (amount <= 0 || amount > available) throw new Error("El monto supera el sobrante de la quincena anterior.");
  const target = snapshot.weeks.find((week) => week.status === "active");
  if (!target) throw new Error("No hay una semana activa en Firebase.");
  return {
    ...snapshot,
    weeks: snapshot.weeks.map((week) => week.id === target.id ? {
      ...week,
      availableCents: week.availableCents + amount,
      budgetCents: week.budgetCents + amount,
      fundedCents: (week.fundedCents ?? week.budgetCents) + amount,
      previousPeriodCarryoverCents: (week.previousPeriodCarryoverCents ?? 0) + amount,
    } : week),
    totalBudgetCents: snapshot.totalBudgetCents + amount,
    previousPeriodCarryover: {
      ...carryover,
      availableCents: available - amount,
      transferredCents: carryover.transferredCents + amount,
    },
  };
}

/** Asigna dinero a la semana activa indicando si acaba de entrar a Nu o ya existía como aporte temporal. */
export function applyWeekFunding(
  snapshot: FinancialSnapshot,
  amountCents: number,
  source: "new" | "temporary",
): FinancialSnapshot {
  const amount = Math.round(amountCents);
  if (amount <= 0) throw new Error("El monto debe ser mayor que cero.");
  if (source === "temporary" && amount > snapshot.temporaryContributionCents) {
    throw new Error("El aporte temporal neto no alcanza para agregar ese dinero.");
  }
  const target = snapshot.weeks.find((week) => week.status === "active");
  if (!target) throw new Error("No hay una semana activa en Firebase.");
  return {
    ...snapshot,
    accounts: source === "new"
      ? snapshot.accounts.map((account, index) => index === 0 ? { ...account, balanceCents: account.balanceCents + amount } : account)
      : snapshot.accounts,
    weeks: snapshot.weeks.map((week) => week.id === target.id ? {
      ...week,
      availableCents: week.availableCents + amount,
      budgetCents: week.budgetCents + amount,
      fundedCents: (week.fundedCents ?? week.budgetCents) + amount,
      otherAdjustmentsCents: (week.otherAdjustmentsCents ?? 0) + amount,
    } : week),
    totalBudgetCents: snapshot.totalBudgetCents + amount,
    temporaryContributionCents: source === "temporary"
      ? snapshot.temporaryContributionCents - amount
      : snapshot.temporaryContributionCents,
  };
}

/** Ajusta la envolvente semanal cuando el dinero se mueve entre semana y ahorro. */
export function applySavingsWeekTransfer(
  snapshot: FinancialSnapshot,
  amountCents: number,
  direction: "to_savings" | "to_week",
): FinancialSnapshot {
  const amount = Math.round(amountCents);
  if (amount <= 0) throw new Error("El monto debe ser mayor que cero.");
  const target = snapshot.weeks.find((week) => week.status === "active");
  if (!target) throw new Error("No hay una semana activa en Firebase.");
  if (direction === "to_savings" && amount > target.availableCents) {
    throw new Error("La semana activa no tiene saldo suficiente.");
  }
  return {
    ...snapshot,
    weeks: snapshot.weeks.map((week) => {
      if (week.id !== target.id) return week;
      if (direction === "to_savings") {
        return {
          ...week,
          availableCents: week.availableCents - amount,
          spentCents: week.spentCents + amount,
          savingsAppliedCents: (week.savingsAppliedCents ?? 0) + amount,
        };
      }
      return {
        ...week,
        availableCents: week.availableCents + amount,
        budgetCents: week.budgetCents + amount,
        fundedCents: (week.fundedCents ?? week.budgetCents) + amount,
        otherAdjustmentsCents: (week.otherAdjustmentsCents ?? 0) + amount,
      };
    }),
    totalBudgetCents: snapshot.totalBudgetCents + (direction === "to_week" ? amount : 0),
  };
}

export interface TemporaryAllocation {
  id: string;
  description: string;
  beforeCents: number;
  usedCents: number;
  afterCents: number;
}

export function temporaryContributionTotal(items: TemporaryContribution[]): number {
  return items
    .filter((item) => item.active !== false)
    .reduce((sum, item) => sum + Math.round(Number(item.balanceCents) || 0), 0);
}

export type TemporaryContributionChange =
  | { type: "adjustment"; deltaCents: number }
  | { type: "rename"; description: string }
  | { type: "cancel" };

export function applyTemporaryContributionChange(
  totalCents: number,
  contribution: TemporaryContribution,
  change: TemporaryContributionChange,
): { contribution: TemporaryContribution; totalCents: number; amountDeltaCents: number } {
  if (contribution.active === false) throw new Error("Esta aportación ya está cancelada.");
  if (change.type === "rename") {
    const description = change.description.trim();
    if (!description) throw new Error("Escribe una descripción para identificar este dinero.");
    return { contribution: { ...contribution, description }, totalCents, amountDeltaCents: 0 };
  }

  const amountDeltaCents = change.type === "cancel" ? -contribution.balanceCents : Math.round(change.deltaCents);
  if (change.type === "adjustment" && !amountDeltaCents) throw new Error("El ajuste debe ser distinto de cero.");
  if (change.type === "adjustment" && contribution.balanceCents + amountDeltaCents < 0) {
    throw new Error("El retiro supera el saldo de esta aportación.");
  }
  const nextTotal = Math.round(totalCents) + amountDeltaCents;
  if (nextTotal < 0) throw new Error("El ajuste dejaría el aporte temporal neto por debajo de cero.");
  return {
    contribution: {
      ...contribution,
      balanceCents: change.type === "cancel" ? 0 : contribution.balanceCents + amountDeltaCents,
      active: change.type !== "cancel",
    },
    totalCents: nextTotal,
    amountDeltaCents,
  };
}

/** Reclasifica un fondo individual hacia su destino sin duplicar dinero dentro de Nu. */
export function applyTemporaryContributionDestination(
  snapshot: FinancialSnapshot,
  contribution: TemporaryContribution,
  amountCents: number,
  destination: TemporaryContributionDestination,
  destinationContribution?: TemporaryContribution,
): { snapshot: FinancialSnapshot; contribution: TemporaryContribution; destinationContribution?: TemporaryContribution; accountDeltaCents: number } {
  const amount = Math.round(amountCents);
  if (amount <= 0) throw new Error("El importe debe ser mayor que cero.");
  const changed = applyTemporaryContributionChange(snapshot.temporaryContributionCents, contribution, {
    type: "adjustment",
    deltaCents: -amount,
  });

  if (destination === "fund") {
    if (!destinationContribution) throw new Error("Elige un fondo receptor.");
    if (destinationContribution.id === contribution.id) throw new Error("Elige otro fondo como receptor.");
    if (destinationContribution.active === false) throw new Error("El fondo receptor está archivado.");
    const received = applyTemporaryContributionChange(changed.totalCents, destinationContribution, {
      type: "adjustment",
      deltaCents: amount,
    });
    return {
      snapshot: { ...snapshot, temporaryContributionCents: received.totalCents },
      contribution: changed.contribution,
      destinationContribution: received.contribution,
      accountDeltaCents: 0,
    };
  }

  if (destination === "week") {
    return {
      snapshot: applyWeekFunding(snapshot, amount, "temporary"),
      contribution: changed.contribution,
      accountDeltaCents: 0,
    };
  }

  const reclassified = { ...snapshot, temporaryContributionCents: changed.totalCents };
  if (destination === "savings") {
    return {
      snapshot: {
        ...reclassified,
        savingsCents: snapshot.savingsCents + amount,
        savingsThisPeriodCents: snapshot.savingsThisPeriodCents + amount,
      },
      contribution: changed.contribution,
      accountDeltaCents: 0,
    };
  }

  if (destination === "outside") {
    const nuBalanceCents = snapshot.accounts[0]?.balanceCents ?? 0;
    if (amount > nuBalanceCents) throw new Error("El saldo de Nu no alcanza para sacar ese dinero de Banculem.");
    return {
      snapshot: {
        ...reclassified,
        accounts: snapshot.accounts.map((account, index) => index === 0
          ? { ...account, balanceCents: account.balanceCents - amount }
          : account),
      },
      contribution: changed.contribution,
      accountDeltaCents: -amount,
    };
  }

  if (destination !== "classification") throw new Error("El destino del fondo no es válido.");
  return { snapshot: reclassified, contribution: changed.contribution, accountDeltaCents: 0 };
}

export function allocateTemporaryContributions(
  items: TemporaryContribution[],
  selectedIds: string[],
  amountCents: number,
): { contributions: TemporaryContribution[]; allocations: TemporaryAllocation[] } {
  const amount = Math.round(amountCents);
  if (amount <= 0) throw new Error("El presupuesto debe ser mayor que cero.");
  const netAvailable = Math.max(0, temporaryContributionTotal(items));
  if (amount > netAvailable) throw new Error("El aporte temporal neto no alcanza para ese presupuesto.");

  const selected = [...new Set(selectedIds)];
  if (!selected.length) throw new Error("Elige al menos una aportación temporal.");
  let pending = amount;
  const allocations: TemporaryAllocation[] = [];
  const updatedById = new Map<string, TemporaryContribution>();

  for (const id of selected) {
    if (pending <= 0) break;
    const item = items.find((candidate) => candidate.id === id && candidate.active !== false);
    if (!item || item.balanceCents <= 0) throw new Error("Una aportación seleccionada ya no está disponible.");
    const usedCents = Math.min(item.balanceCents, pending);
    const next = { ...item, balanceCents: item.balanceCents - usedCents };
    updatedById.set(id, next);
    allocations.push({
      id,
      description: item.description,
      beforeCents: item.balanceCents,
      usedCents,
      afterCents: next.balanceCents,
    });
    pending -= usedCents;
  }
  if (pending > 0) throw new Error("Las aportaciones elegidas no alcanzan para ese presupuesto.");
  return {
    contributions: items.map((item) => updatedById.get(item.id) ?? item),
    allocations,
  };
}

export interface PeriodFundingAllocation {
  weekId: WeekBalance["id"];
  label: string;
  amountCents: number;
}

export function applyPendingPeriodFunding(
  snapshot: FinancialSnapshot,
  amountCents: number,
  addToNu: boolean,
): { snapshot: FinancialSnapshot; allocations: PeriodFundingAllocation[] } {
  const amount = Math.round(amountCents);
  const pendingTotal = Math.max(0, snapshot.pendingPeriodFundingCents
    ?? snapshot.weeks.reduce((sum, week) => sum + Math.max(0, week.pendingFundingCents ?? 0), 0));
  if (amount <= 0) throw new Error("El depósito debe ser mayor que cero.");
  if (!pendingTotal) throw new Error("Esta quincena ya tiene todo su presupuesto asignado.");
  if (amount > pendingTotal) throw new Error("El depósito supera el presupuesto que falta por asignar.");

  let remaining = amount;
  const allocations: PeriodFundingAllocation[] = [];
  const weeks = snapshot.weeks.map((week) => {
    const pending = Math.max(0, week.pendingFundingCents ?? 0);
    const applied = Math.min(pending, remaining);
    remaining -= applied;
    if (applied > 0) allocations.push({ weekId: week.id, label: week.label, amountCents: applied });
    return {
      ...week,
      availableCents: week.availableCents + applied,
      fundedCents: Math.min(week.budgetCents, (week.fundedCents ?? Math.max(0, week.budgetCents - pending)) + applied),
      pendingFundingCents: pending - applied,
    };
  });
  if (remaining > 0) throw new Error("No pude repartir todo el depósito entre las semanas.");

  return {
    snapshot: {
      ...snapshot,
      accounts: addToNu
        ? snapshot.accounts.map((account, index) => index === 0 ? { ...account, balanceCents: account.balanceCents + amount } : account)
        : snapshot.accounts,
      weeks,
      pendingPeriodFundingCents: pendingTotal - amount,
      periodFundingMode: pendingTotal === amount ? "deposited" : "pending_deposit",
    },
    allocations,
  };
}
